import { Close as CloseIcon } from "@mui/icons-material";
import {
  Box,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";

import Currency from "../../common/Currency";
import {
  CodeType,
  DiscountType,
  FeeType,
  PromotionCodeDetail,
} from "../../../types/admin";
import { formatDate } from "../../../utils/formatters";

interface PromotionCodeViewModalProps {
  open: boolean;
  codeDetail: PromotionCodeDetail | null;
  loading?: boolean;
  onClose: () => void;
}

const PromotionCodeViewModal: React.FC<PromotionCodeViewModalProps> = ({
  open,
  codeDetail,
  loading = false,
  onClose,
}) => {
  const { t } = useTranslation();

  if (!codeDetail && !loading) {
    return null;
  }

  const getDiscountTypeLabel = (type: DiscountType) => {
    return type === DiscountType.Percentage ? t("admin.percentage") : t("admin.fixedAmount");
  };

  const getFeeTypeLabel = (type: FeeType) => {
    switch (type) {
      case FeeType.ServiceFee:
        return t("admin.serviceFee");
      case FeeType.CargoFee:
        return t("admin.cargoFee");
      case FeeType.AllFees:
        return t("admin.allFees");
      default:
        return t("admin.unknown");
    }
  };

  const getCodeTypeLabel = (type: CodeType) => {
    switch (type) {
      case CodeType.FirstTime:
        return t("admin.firstTime");
      case CodeType.Seasonal:
        return t("admin.seasonal");
      case CodeType.Loyalty:
        return t("admin.loyalty");
      case CodeType.Referral:
        return t("admin.referral");
      case CodeType.General:
        return t("admin.general");
      default:
        return t("admin.unknown");
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography variant="h6">
            {t("admin.promotionCodeDetails")} - {codeDetail?.code || t("common.loading")}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box sx={{ p: 4, textAlign: "center" }}>
            <Typography>{t("admin.loadingPromotionCodeDetails")}</Typography>
          </Box>
        ) : codeDetail ? (
          <Grid container spacing={3}>
            {/* Basic Information */}
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  {t("admin.basicInformation")}
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="body2" color="textSecondary">
                      {t("admin.code")}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{ fontFamily: "monospace", fontWeight: 600 }}
                    >
                      {codeDetail.code}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body2" color="textSecondary">
                      {t("admin.description")}
                    </Typography>
                    <Typography variant="body1">
                      {codeDetail.description}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary">
                      {t("admin.status")}
                    </Typography>
                    <Box sx={{ mt: 0.5 }}>
                      <Chip
                        label={codeDetail.isActive ? t("admin.active") : t("admin.inactive")}
                        color={codeDetail.isActive ? "success" : "default"}
                        size="small"
                      />
                      {codeDetail.isExpired && (
                        <Chip
                          label={t("admin.expired")}
                          color="error"
                          size="small"
                          sx={{ ml: 1 }}
                        />
                      )}
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary">
                      {t("admin.codeType")}
                    </Typography>
                    <Typography variant="body1">
                      {getCodeTypeLabel(codeDetail.codeType)}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            {/* Discount Configuration */}
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  {t("admin.discountConfiguration")}
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary">
                      {t("admin.discountType")}
                    </Typography>
                    <Typography variant="body1">
                      {getDiscountTypeLabel(codeDetail.discountType)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary">
                      {t("admin.discountValue")}
                    </Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {codeDetail.discountType === DiscountType.Percentage ? (
                        `${codeDetail.discountValue}%`
                      ) : (
                        <Currency amount={codeDetail.discountValue} />
                      )}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body2" color="textSecondary">
                      {t("admin.appliesTo")}
                    </Typography>
                    <Typography variant="body1">
                      {getFeeTypeLabel(codeDetail.feeType)}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            {/* Usage Limits */}
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  {t("admin.usageLimits")}
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary">
                      {t("admin.maxTotalUses")}
                    </Typography>
                    <Typography variant="body1">
                      {codeDetail.maxTotalUses ?? t("admin.unlimited")}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary">
                      {t("admin.currentUses")}
                    </Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {codeDetail.currentTotalUses}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary">
                      {t("admin.remainingUses")}
                    </Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {codeDetail.remainingUses === Number.MAX_SAFE_INTEGER
                        ? t("admin.unlimited")
                        : codeDetail.remainingUses}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary">
                      {t("admin.maxPerUser")}
                    </Typography>
                    <Typography variant="body1">
                      {codeDetail.maxUsesPerUser ?? t("admin.unlimited")}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            {/* Dates & Restrictions */}
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  {t("admin.datesAndRestrictions")}
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary">
                      {t("admin.startDate")}
                    </Typography>
                    <Typography variant="body1">
                      {codeDetail.startDate
                        ? formatDate(codeDetail.startDate)
                        : t("admin.noStartDate")}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary">
                      {t("admin.endDate")}
                    </Typography>
                    <Typography variant="body1">
                      {codeDetail.endDate
                        ? formatDate(codeDetail.endDate)
                        : t("admin.noExpiration")}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary">
                      {t("admin.minOrderValue")}
                    </Typography>
                    <Typography variant="body1">
                      {codeDetail.minOrderValue ? (
                        <Currency amount={codeDetail.minOrderValue} />
                      ) : (
                        t("admin.noMinimum")
                      )}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary">
                      {t("admin.routeRestrictions")}
                    </Typography>
                    <Typography variant="body1">
                      {codeDetail.routeRestrictions || t("admin.none")}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            {/* Usage History */}
            {codeDetail.usages && codeDetail.usages.length > 0 && (
              <Grid item xs={12}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    {t("admin.usageHistory")} ({codeDetail.usages.length})
                  </Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>{t("admin.user")}</TableCell>
                          <TableCell>{t("admin.orderNumber")}</TableCell>
                          <TableCell>{t("admin.discountApplied")}</TableCell>
                          <TableCell>{t("admin.usedAt")}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {codeDetail.usages.map((usage) => (
                          <TableRow key={usage.id}>
                            <TableCell>
                              <Box>
                                <Typography variant="body2">
                                  {usage.userName || t("admin.unknownUser")}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="textSecondary"
                                >
                                  {usage.userEmail}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell>{usage.orderNumber}</TableCell>
                            <TableCell>
                              <Currency amount={usage.discountAmountApplied} />
                            </TableCell>
                            <TableCell>{formatDate(usage.usedAt)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              </Grid>
            )}

            {/* Metadata */}
            <Grid item xs={12}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  {t("admin.metadata")}
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6} md={3}>
                    <Typography variant="body2" color="textSecondary">
                      {t("admin.createdBy")}
                    </Typography>
                    <Typography variant="body1">
                      {codeDetail.createdBy || t("admin.system")}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant="body2" color="textSecondary">
                      {t("admin.createdAt")}
                    </Typography>
                    <Typography variant="body1">
                      {formatDate(codeDetail.createdAt)}
                    </Typography>
                  </Grid>
                  {codeDetail.updatedAt && (
                    <Grid item xs={6} md={3}>
                      <Typography variant="body2" color="textSecondary">
                        {t("admin.updatedAt")}
                      </Typography>
                      <Typography variant="body1">
                        {formatDate(codeDetail.updatedAt)}
                      </Typography>
                    </Grid>
                  )}
                  {codeDetail.notes && (
                    <Grid item xs={12}>
                      <Typography variant="body2" color="textSecondary">
                        {t("admin.notes")}
                      </Typography>
                      <Typography variant="body1">
                        {codeDetail.notes}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </Paper>
            </Grid>
          </Grid>
        ) : (
          <Box sx={{ p: 4, textAlign: "center" }}>
            <Typography color="error">
              {t("admin.failedToLoadPromotionCodeDetails")}
            </Typography>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PromotionCodeViewModal;
